import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { ErrorMessage, Formik, useFormikContext } from "formik";
import { FC, FormEvent, memo, useContext, useEffect, useState } from "react";
import * as yup from "yup";

import appStyles from "../../../App.module.css";
import ScrollToFirstError from "../../../common-components/ScrollToFirstError";
import { BusinessTravelContext } from "../../BusinessTravel";
import btConstants, { businessTravelEUDocs, businessTravelUKDocs } from "../../config/bt-constants";
import { IBusinessTravelContext, IConfirmation } from "../../config/interfaces";
import Popup from "../Popup";
import QuoteApi from "../api/QuoteApi";
import Footer from "../common/Footer";
import QuoteSummary from "../common/QuoteSummary";
import CardIcon from "../icons/CardIcon";
import InvoiceIcon from "../icons/InvoiceIcon";
import LoadingModal from "../modal/LoadingModal";

interface OrderFormProps {}
const stripePromise = loadStripe(btConstants.BTpublishableKey!);

const PaymentForm: FC<OrderFormProps> = (props: OrderFormProps) => {
  const { order, previous, version, setIsPaymentInvoice, quoteData } = useContext(
    BusinessTravelContext
  ) as IBusinessTravelContext;

  const [loading, setLoading] = useState<boolean>(false);
  const [stripeIntentData, setStripeIntentData] = useState<any>(null);

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const init = async () => {
    try {
      setLoading(true);
      const response: any = await QuoteApi.getPaymentIntent(order.order_number);
      setStripeIntentData(response.data);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const initialFormValues: IConfirmation = {
    acceptAgreement: false,
    acceptDisclosure: false,
    subscriptionPreferences: false,
  };

  const validationSchema = yup.object().shape({
    acceptAgreement: yup.boolean().required().oneOf([true], "You must agree the  statement."),
    acceptDisclosure: yup.boolean().required().oneOf([true], "You must accept disclosure & the terms and conditions."),
    subscriptionPreferences: yup.boolean().notRequired(),
  });

  const handleFormSubmit = async (values: IConfirmation) => {
    try {
      setLoading(true);
    } catch (e: any) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <Popup
          onClose={() => {
            // setShowLoadingModal(false);
          }}
          title=""
          children={<LoadingModal />}
          closeButton={false}
          LoadingModal={true}
        />
      )}

      <Formik
        enableReinitialize={true}
        initialValues={initialFormValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({ values, touched, errors, setFieldValue, handleBlur }) => (
          <div className={`${appStyles.mainContainer} ${appStyles.btMainContainer}`}>
            <span
              className={appStyles.backIcon}
              onClick={() => {
                previous();
                setIsPaymentInvoice(false);
              }}
            />
            <div className={`${appStyles.quoteResultWrapper} ${appStyles.btQuoteResultWrapper}`}>
              <div className={`${appStyles.quoteDetailsBox} ${appStyles.businessBorderBox}`}>
                <h3 className={appStyles.btTitle}>Confirmation</h3>

                <div className={` ${appStyles.quoteFormOtherTravelerInformation}`}>
                  <div className={appStyles.formRow}>
                    <p className={`${appStyles.formLabelHelper} ${appStyles.btFormLabelHelper}`}>
                      <br />
                      To purchase battleface Business Travel Medical Insurance, on behalf of the company, I confirm,
                      understand and agree the following statements: 
                    </p>
                  </div>
                  <div className={appStyles.formRow}>
                    <ul className={`${appStyles.formLabelHelper} ${appStyles.btFormHelperList}`}>
                      <li>No manual work is to be undertaken on any trips to be insured under this policy;</li>
                      <li>
                        In the last 5 years we have had no more than 2 claims against any business travel insurance
                        policy, of more than {quoteData?.currency} 500 in value per claim;
                      </li>
                      <li>
                        The company will advise battleface if we are likely to exceed the number of trips selected for
                        this policy, by more than 10%, and this may result in an increased premium to cover all the
                        eligible trips;
                      </li>
                      <li>Benefits under this policy cannot be amended;</li>
                      <li>
                        The Temporary Total Disablement weekly benefit cannot exceed more than 100% of the insured
                        persons gross weekly wage or {quoteData?.currency} 250 whichever is less. Temporary Partial
                        Disablement cannot exceed more than 50% of the insured persons gross weekly wage or{" "}
                        {quoteData?.currency} 125 whichever is less. 
                      </li>
                    </ul>
                  </div>
                  {/* first  */}
                  <div className={appStyles.formRow}>
                    <div className={`${appStyles.formBlock} ${appStyles.btFormBlock}`}>
                      <div
                        className={`${appStyles.formField} ${
                          errors.acceptAgreement && touched.acceptAgreement && appStyles.hasError
                        }`}
                      >
                        <div className={`${appStyles.bfCheckBox} ${appStyles.btCheckBox}`}>
                          <label
                            htmlFor="acceptAgreement"
                            className={`${appStyles.formLabelHelper}
                             ${appStyles.btFormLabelHelper}`}
                          >
                            <input
                              className={`${appStyles.btInputCheckBox}`}
                              type="checkbox"
                              id="acceptAgreement"
                              name="acceptAgreement"
                              checked={values.acceptAgreement}
                              onBlur={handleBlur}
                              onChange={(e) => {
                                setFieldValue("acceptAgreement", e.target.checked);
                              }}
                            />
                            <span className={`${appStyles.bfCheckmark} ${appStyles.btCheckmark}`}></span>I have read and
                            confirm the above statements.
                            <span className={`${appStyles.btStartIcon}`}>*</span>
                          </label>
                          <ErrorMessage name="acceptAgreement">
                            {(msg) => <div className={appStyles.formError}>{msg}</div>}
                          </ErrorMessage>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* second ? */}

                  <div className={appStyles.formRow}>
                    <div className={`${appStyles.formBlock} ${appStyles.btFormBlock}`}>
                      <div
                        className={`${appStyles.formField} ${
                          errors.acceptDisclosure && touched.acceptDisclosure && appStyles.hasError
                        }`}
                      >
                        <div className={`${appStyles.bfCheckBox} ${appStyles.btCheckBox}`}>
                          <label
                            htmlFor="acceptDisclosure"
                            className={`${appStyles.formLabelHelper}
                                                          ${appStyles.btFormLabelHelper}`}
                          >
                            <input
                              className={`${appStyles.btInputCheckBox}`}
                              type="checkbox"
                              id="acceptDisclosure"
                              name="acceptDisclosure"
                              checked={values.acceptDisclosure}
                              onBlur={handleBlur}
                              onChange={(e) => {
                                setFieldValue("acceptDisclosure", e.target.checked);
                              }}
                            />
                            <span className={`${appStyles.bfCheckmark} ${appStyles.btCheckmark}`}></span>I have read and
                            agree to the{" "}
                            <a
                              className={appStyles.btLink}
                              href={
                                version === "UK"
                                  ? businessTravelUKDocs.insuranceDisclosure
                                  : businessTravelEUDocs.insuranceDisclosure
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Insurance Disclosures
                            </a>
                            ,{" "}
                            <a
                              className={appStyles.btLink}
                              href={
                                version === "UK"
                                  ? businessTravelUKDocs.termsAndConditions
                                  : businessTravelEUDocs.termsAndConditions
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Terms and Conditions
                            </a>
                            , and{" "}
                            <a
                              className={appStyles.btLink}
                              href={
                                version === "UK"
                                  ? businessTravelUKDocs.customerCharter
                                  : businessTravelEUDocs.customerCharter
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Customer Charter
                            </a>
                            .<span className={`${appStyles.btStartIcon}`}>*</span>
                          </label>
                          <ErrorMessage name="acceptDisclosure">
                            {(msg) => <div className={appStyles.formError}>{msg}</div>}
                          </ErrorMessage>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* third? */}

                  <div className={appStyles.formRow}>
                    <div className={`${appStyles.formBlock} ${appStyles.btFormBlock}`}>
                      <div
                        className={`${appStyles.formField} ${
                          errors.subscriptionPreferences && touched.subscriptionPreferences && appStyles.hasError
                        }`}
                      >
                        <div className={`${appStyles.bfCheckBox} ${appStyles.btCheckBox}`}>
                          <label
                            htmlFor="subscriptionPreferences"
                            className={`${appStyles.formLabelHelper}
                                                          ${appStyles.btFormLabelHelper}`}
                          >
                            <input
                              className={`${appStyles.btInputCheckBox}`}
                              type="checkbox"
                              id="subscriptionPreferences"
                              name="subscriptionPreferences"
                              checked={values.subscriptionPreferences}
                              onBlur={handleBlur}
                              onChange={(e) => {
                                setFieldValue("subscriptionPreferences", e.target.checked);
                              }}
                            />
                            <span className={`${appStyles.bfCheckmark} ${appStyles.btCheckmark}`}></span>I agree to
                            store my name and email address to receive renewal emails, updates, newsletters and
                            commercial offers from battleface.
                          </label>
                          <ErrorMessage name="subscriptionPreferences">
                            {(msg) => <div className={appStyles.formError}>{msg}</div>}
                          </ErrorMessage>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <hr className={appStyles.btHrStyle} />

                <div className={`${appStyles.formBlock} ${appStyles.bfMarginTop}`}>
                  {!loading && stripeIntentData && (
                    <Elements
                      stripe={stripePromise}
                      options={{
                        clientSecret: stripeIntentData.client_secret,
                      }}
                    >
                      <PaymentFormElement />
                    </Elements>
                  )}
                </div>
                <Footer />
              </div>

              <QuoteSummary Premium={true} />
            </div>
          </div>
        )}
      </Formik>
    </>
  );
};

export default memo(PaymentForm);

type FormProps = {};

const PaymentFormElement: FC<FormProps> = (props: FormProps) => {
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isStripeLoading, setIsStripeLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { handleSubmit, validateForm, values, submitCount, errors, isValid } = useFormikContext<IConfirmation>();

  const { next, setIsPaymentInvoice, isPaymentInvoice, order, setLoader, loader, version } = useContext(
    BusinessTravelContext
  ) as IBusinessTravelContext;

  const stripe = useStripe();
  const elements: any = useElements();

  const handlePaymentSubmit = async (event: FormEvent<HTMLFormElement>) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    setLoader("payment");
    event.preventDefault();
    await validateForm();
    handleSubmit();
    const isError: any = await validateForm(values);

    if (Object.keys(isError).length > 0) {
      setLoader(null);
      return;
    }

    if (isPaymentInvoice) {
      try {
        const response = await QuoteApi.paymentInvoice(order.order_number);
        if (response.errors) {
          throw response;
        }
        next();
      } catch (error: any) {
        console.log(error.message);
        setErrorMessage(error.message);
      } finally {
        setLoader(null);
      }
    } else {
      if (!stripe || !elements) {
        // Stripe.js hasn't yet loaded.
        // Make sure to disable form submission until Stripe.js has loaded.
        setLoader(null);
        return;
      }

      setIsProcessingPayment(true);

      const { error } = await stripe.confirmPayment({
        //`Elements` instance that was used to create the Payment Element
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: "",
        },
      });

      // // This point will only be reached if there is an immediate error when
      // // confirming the payment. Otherwise, your customer will be redirected to
      // // your `return_url`. For some payment methods like iDEAL, your customer will
      // // be redirected to an intermediate site first to authorize the payment, then
      // // redirected to the `return_url`.

      if (error) {
        if (error.type === "card_error" || error.type === "validation_error") {
          setErrorMessage(error.message);
        } else {
          setErrorMessage("An unexpected error occurred.");
        }
        setLoader(null);
        setIsProcessingPayment(false);
        return;
      } else {
        setIsProcessingPayment(false);
        setLoader(null);
        next();
      }
    }
  };

  return (
    <>
      {loader && (
        <Popup onClose={() => {}} title="" children={<LoadingModal />} closeButton={false} LoadingModal={true} />
      )}

      <form onSubmit={handlePaymentSubmit}>
        <ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />
        <div className={` ${appStyles.formBox} ${appStyles.formBlock}`}>
          <div className={appStyles.pageSubTitle}>
            <h2 className={appStyles.colorPrimary}>Payment Information</h2>
          </div>
          <div className={` ${appStyles.btPaymentInfoTabWrap}`}>
            <div
              onClick={() => {
                setIsPaymentInvoice(false);
              }}
              className={`${appStyles.btPaymentInfoTab}  ${!isPaymentInvoice ? appStyles.btPaymentInfoTabActive : ""}`}
            >
              <CardIcon /> Card
            </div>
            <div
              className={`${appStyles.btPaymentInfoTab} ${isPaymentInvoice ? appStyles.btPaymentInfoTabActive : ""}`}
              onClick={async () => {
                setIsPaymentInvoice(true);
                setIsStripeLoading(false);
                setErrorMessage(undefined);
              }}
            >
              <InvoiceIcon /> Invoice
            </div>
          </div>
          {isPaymentInvoice ? (
            <div className={appStyles.paymentInvoiceDetailWrapper}>
              <p>
                We will send an invoice to the email address provided within 2 working days. Please arrange payment
                within 5 working days of receipt. If you do not receive your invoice please contact us at&nbsp;
                {version === "UK" ? btConstants.UKTelephone : btConstants.EUTelephone}
                &nbsp;or by emailing {version === "UK" ? btConstants.UKEmail : btConstants.EUEmail}.
              </p>
              <p>
                If payment is not received within 5 working days of the invoice being sent, the quote will be cancelled
                and you will have to raise a new quote. Prices may differ.
              </p>
            </div>
          ) : (
            <div className={`${appStyles.formRow} ${appStyles.paymentCardWrapper}`}>
              <PaymentElement
                onReady={() => {
                  setIsStripeLoading(false);
                }}
              />
            </div>
          )}
        </div>

        {!isStripeLoading && (
          <div className={`${appStyles.paymentButtonWrapper} ${appStyles.formRow}`}>
            <div className={`${appStyles.buttonBlock} `}>
              <button
                className={`${appStyles.bfButton} ${appStyles.btButton100} ${appStyles.mT24} `}
                type="submit"
                disabled={isProcessingPayment || !stripe || !elements}
              >
                {isPaymentInvoice ? "PROCEED WITH INVOICE" : "Confirm and pay"}
              </button>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className={appStyles.formRow}>
            <div className={appStyles.battlefaceServerError}>{errorMessage}</div>
          </div>
        )}
      </form>
    </>
  );
};
